export enum EnvKeys {
  VITE_APP_URL = 'VITE_APP_URL',
  VITE_SERVER_URL = 'VITE_SERVER_URL',
  VITE_SENTRY_DSN = 'VITE_SENTRY_DSN',
  VITE_SENTRY_AUTH_TOKEN = 'VITE_SENTRY_AUTH_TOKEN',
  VITE_SENTRY_ENVIRONMENT = 'VITE_SENTRY_ENVIRONMENT',
  VITE_RELEASE_TAG = 'VITE_RELEASE_TAG',
  VITE_GQL_SCHEMA_URL = 'VITE_GQL_SCHEMA_URL',
  VITE_SEGMENT_KEY = 'VITE_SEGMENT_KEY',
  VITE_LAUNCH_DARKLY_KEY = 'VITE_LAUNCH_DARKLY_KEY',
  VITE_LOG_ROCKET_ROOT_HOSTNAME = 'VITE_LOG_ROCKET_ROOT_HOSTNAME',
  VITE_LOG_ROCKET_KEY = 'VITE_LOG_ROCKET_KEY',
  VITE_INTERVIEW_API_URL = 'VITE_INTERVIEW_API_URL',
  VITE_DATADOG_APPLICATION_ID = 'VITE_DATADOG_APPLICATION_ID',
  VITE_DATADOG_CLIENT_TOKEN = 'VITE_DATADOG_CLIENT_TOKEN',
  VITE_DATADOG_SERVICE = 'VITE_DATADOG_SERVICE',
  VITE_DATADOG_SITE = 'VITE_DATADOG_SITE',
}

// Enum for different environments
export enum Environment {
  LOCAL = 'LOCAL',
  STAGING = 'STAGING',
  PROD = 'PROD',
}

// Define the global Window interface
declare global {
  interface Window {
    interviewCanStartTimeoutId: NodeJS.Timeout | undefined;
    env?: {
      VITE_APP_URL: string;
      VITE_SERVER_URL: string;
      VITE_SENTRY_DSN: string;
      VITE_SENTRY_AUTH_TOKEN: string;
      VITE_SENTRY_ENVIRONMENT: string;
      VITE_RELEASE_TAG: string;
      VITE_GQL_SCHEMA_URL: string;
      VITE_SEGMENT_KEY: string;
      VITE_LAUNCH_DARKLY_KEY: string;
      VITE_LOG_ROCKET_ROOT_HOSTNAME: string;
      VITE_LOG_ROCKET_KEY: string;
      VITE_INTERVIEW_API_URL: string;
      VITE_DATADOG_APPLICATION_ID: string;
      VITE_DATADOG_CLIENT_TOKEN: string;
      VITE_DATADOG_SERVICE: string;
      VITE_DATADOG_SITE: string;
    };
  }
}

// Lock down env to make it immutable
Object.defineProperty(window, 'env', {
  writable: false,
  value: Object.freeze(window.env),
});

export const getEnvVar = (key: string): string | undefined => {
  const prefix = 'VITE_';

  // if `env.js` script don't set "env" (e.g. in development), fallback to import.meta.env instead
  if (!window.env) {
    return import.meta.env[prefix + key];
  }

  return window.env[(prefix + key) as EnvKeys] || '';
};

export function getAppUrl() {
  return getEnvVar('APP_URL');
}

export function getServerUrl() {
  return getEnvVar('SERVER_URL');
}

export function getSentryDsn() {
  return getEnvVar('SENTRY_DSN');
}

export function getSentryEnvironment() {
  return getEnvVar('SENTRY_ENVIRONMENT') || 'local';
}

export function getReleaseTag() {
  return getEnvVar('RELEASE_TAG');
}

export function getGqlSchemaUrl() {
  return getEnvVar('GQL_SCHEMA_URL');
}

export function getSegmentKey() {
  return getEnvVar('SEGMENT_KEY') || '';
}

export function getLaunchDarklyKey() {
  return getEnvVar('LAUNCH_DARKLY_KEY');
}

export function getLogRocketKey() {
  return getEnvVar('LOG_ROCKET_KEY');
}

export function getInterviewApiUrl() {
  return getEnvVar('INTERVIEW_API_URL');
}

export function getEnvironment(): Environment {
  if (window.location.hostname === 'localhost') {
    return Environment.LOCAL;
  }

  if (window.location.hostname === 'assessment.alignerr-stage.xyz') {
    return Environment.STAGING;
  }

  return Environment.PROD;
}

export function getDatadogApplicationId() {
  return getEnvVar('DATADOG_APPLICATION_ID');
}

export function getDatadogClientToken() {
  return getEnvVar('DATADOG_CLIENT_TOKEN');
}

export function getDatadogService() {
  return getEnvVar('DATADOG_SERVICE');
}

export function getDatadogSite() {
  return getEnvVar('DATADOG_SITE');
}

export function getSpeedTestFileUrl() {
  return getEnvVar('SPEED_TEST_FILE_URL') || '/test-file.blob';
}
